import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  X,
  RefreshCw,
  AlertTriangle,
  MapPin,
  Clock,
  Navigation,
  Compass,
  GripVertical,
  Save,
} from 'lucide-react';
import MainService from '../../services/MainService';
import {
  AVG_SPEED_KMH,
  optimizeNearestNeighbor,
  recalculateRouteLegs,
  resolveOfficeStart,
  buildRouteDetailPayload,
  formatDistanceKm,
  formatDurationMin,
  parseCoord,
} from '../../utils/routeOptimize';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const createStopIcon = (label, color) =>
  L.divIcon({
    className: 'route-opt-marker',
    html: `
      <div style="display:flex;flex-direction:column;align-items:center;transform:translateY(-4px);">
        <div style="
          min-width:26px;height:26px;border-radius:999px;display:flex;align-items:center;justify-content:center;
          background:${color};color:#fff;font-size:12px;font-weight:700;
          border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.35);
        ">${label}</div>
      </div>
    `,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
    popupAnchor: [0, -14],
  });

const FitBounds = ({ points }) => {
  const map = useMap();
  useEffect(() => {
    if (!points?.length) return;
    if (points.length === 1) {
      map.setView(points[0], 12);
      return;
    }
    map.fitBounds(L.latLngBounds(points), { padding: [40, 40], maxZoom: 14 });
  }, [map, points]);
  return null;
};

const normalizeRoutingRow = (r) => ({
  id: r.Id,
  orderId: r.OrderId,
  voucherNo: r.VoucherNo || '',
  customerName: r.CustomerName || '',
  customerMobile: r.CustomerMobileNo || '',
  locationName: r.DeliveryLocation || '',
  emirateName: r.DeliveryEmirate || '',
  address: r.DeliveryAddress || '',
  statusName: r.StatusName || '',
  lat: parseCoord(r.DeliveryLAT),
  lng: parseCoord(r.DeliveryLNG),
  locationOrder: Number(r.LocationOrder) || 0,
  timeFromPrevious: Number(r.TimeFromPrevoous) || 0,
  distanceFromPrevious: Number(r.DistanceFromPrevious) || 0,
  cumulativeDistance: Number(r.CumilativeDistance) || 0,
});

const hasSavedRoute = (rows) => rows.some((r) => Number(r.locationOrder) > 0);

const buildResultFromSaved = (rows, start, speedKmh = AVG_SPEED_KMH) => {
  const sorted = [...rows]
    .filter((r) => Number(r.locationOrder) > 0)
    .sort((a, b) => a.locationOrder - b.locationOrder);
  const withCoords = [];
  const withoutCoords = [];
  sorted.forEach((r) => {
    if (r.lat != null && r.lng != null) withCoords.push(r);
    else withoutCoords.push(r);
  });
  // Include unmapped that were never ordered
  rows.forEach((r) => {
    if (!Number(r.locationOrder) && (r.lat == null || r.lng == null)) {
      if (!withoutCoords.find((x) => x.id === r.id)) withoutCoords.push(r);
    }
  });

  const route = withCoords.map((stop, index) => ({
    ...stop,
    stopNo: stop.locationOrder || index + 1,
    distanceFromPrevKm: stop.distanceFromPrevious || 0,
    durationFromPrevMin: stop.timeFromPrevious || 0,
    cumulativeKm: stop.cumulativeDistance || 0,
    cumulativeMin: 0,
  }));

  // Prefer recalculating so drag/reorder stays consistent
  const recalculated = recalculateRouteLegs(withCoords, start, speedKmh);
  const last = recalculated[recalculated.length - 1];

  return {
    start,
    route: recalculated.length ? recalculated : route,
    withoutCoords,
    totalDistanceKm: last?.cumulativeKm || 0,
    totalDurationMin: last?.cumulativeMin || 0,
    speedKmh,
    fromSaved: true,
  };
};

/**
 * @param {'optimize'|'view'} mode
 *   optimize — Delivery Allocation (confirm into parent state)
 *   view — Delivery Notes list (load saved / reroute / save to API)
 */
const OptimizedRouteModal = ({
  open,
  onClose,
  onConfirm,
  deliveryNoteId,
  deliveryNoteLabel,
  mode = 'optimize',
}) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [result, setResult] = useState(null);
  const [officeStart, setOfficeStart] = useState(null);
  const [rawStops, setRawStops] = useState([]);
  const [dirty, setDirty] = useState(false);
  const dragIndexRef = useRef(null);

  const applyOptimize = useCallback((stops, start) => {
    if (!start) {
      setError('Office coordinates are not set. Go to Company Profile → Coordinates and pick the office location.');
      setResult(null);
      return;
    }
    const optimized = optimizeNearestNeighbor(stops, start);
    setResult({ ...optimized, fromSaved: false });
    setDirty(true);
    setInfo('Route optimized from office. Drag rows to rearrange, then save.');
  }, []);

  const load = useCallback(async () => {
    if (!deliveryNoteId) return;
    setLoading(true);
    setError('');
    setInfo('');
    setResult(null);
    setDirty(false);
    try {
      const [profile, data] = await Promise.all([
        MainService.GetCompanyProfile(),
        MainService.GetDeliveryNoteDetailsForRouting(deliveryNoteId),
      ]);
      const start = resolveOfficeStart(profile);
      setOfficeStart(start);

      const rows = (Array.isArray(data) ? data : []).map(normalizeRoutingRow);
      setRawStops(rows);

      if (rows.length === 0) {
        setError('No allocated orders found for this delivery note.');
        return;
      }

      if (!start) {
        setError('Office coordinates are not set. Go to Company Profile → Coordinates and pick the office location on the map.');
        // Still show saved order if any (without recalculating from office)
        if (hasSavedRoute(rows)) {
          const fallbackStart = {
            lat: rows.find((r) => r.lat != null)?.lat || 25.2048,
            lng: rows.find((r) => r.lng != null)?.lng || 55.2708,
            label: 'Start (office not set)',
          };
          setResult(buildResultFromSaved(rows, fallbackStart));
          setInfo('Showing saved sequence. Set office coordinates for accurate distances.');
        }
        return;
      }

      if (mode === 'view' && hasSavedRoute(rows)) {
        setResult(buildResultFromSaved(rows, start));
        setInfo('Showing saved route. Use Reroute to re-optimize, or drag to rearrange.');
      } else {
        applyOptimize(rows, start);
      }
    } catch (err) {
      console.error(err);
      setError('Failed to load route details.');
    } finally {
      setLoading(false);
    }
  }, [deliveryNoteId, mode, applyOptimize]);

  useEffect(() => {
    if (!open || !deliveryNoteId) return undefined;
    load();
    return undefined;
  }, [open, deliveryNoteId, load]);

  const mapPoints = useMemo(() => {
    if (!result) return [];
    const pts = [[result.start.lat, result.start.lng]];
    result.route.forEach((s) => {
      if (s.lat != null && s.lng != null) pts.push([s.lat, s.lng]);
    });
    return pts;
  }, [result]);

  const handleReroute = () => {
    if (!officeStart) {
      setError('Office coordinates are not set. Go to Company Profile → Coordinates first.');
      return;
    }
    applyOptimize(rawStops, officeStart);
  };

  const reorderRoute = (fromIndex, toIndex) => {
    if (!result || fromIndex === toIndex || fromIndex < 0 || toIndex < 0) return;
    const next = [...result.route];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    const start = result.start;
    const recalculated = recalculateRouteLegs(next, start, result.speedKmh || AVG_SPEED_KMH);
    const last = recalculated[recalculated.length - 1];
    setResult({
      ...result,
      route: recalculated,
      totalDistanceKm: last?.cumulativeKm || 0,
      totalDurationMin: last?.cumulativeMin || 0,
      fromSaved: false,
    });
    setDirty(true);
  };

  const onDragStart = (index) => {
    dragIndexRef.current = index;
  };

  const onDragOver = (e) => {
    e.preventDefault();
  };

  const onDrop = (toIndex) => {
    const from = dragIndexRef.current;
    dragIndexRef.current = null;
    if (from == null) return;
    reorderRoute(from, toIndex);
  };

  const handleSave = async () => {
    if (!result || result.route.length === 0) {
      setError('No route stops to save.');
      return;
    }
    const payload = buildRouteDetailPayload(result);

    if (mode === 'optimize' && typeof onConfirm === 'function') {
      onConfirm(payload);
      onClose();
      return;
    }

    // view mode — persist via API
    setSaving(true);
    setError('');
    try {
      const resp = await MainService.UpdateDeliveryNoteRoute({
        Id: deliveryNoteId,
        DeliveryNoteDetails: payload,
      });
      if (resp === 'Success' || resp === true || resp === 'true') {
        setDirty(false);
        setInfo('Route saved successfully.');
        if (typeof onConfirm === 'function') onConfirm(payload);
      } else {
        setError(typeof resp === 'string' ? resp : 'Failed to save route.');
      }
    } catch (err) {
      console.error(err);
      setError('Failed to save route.');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[92vh] flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200 flex items-start justify-between gap-3 bg-[#f0f9f4] rounded-t-lg">
          <div className="flex items-start gap-3">
            <Compass className="w-6 h-6 text-primary mt-0.5" />
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {mode === 'view' ? 'Delivery Route' : 'Optimized Delivery Route'}
              </h3>
              <p className="text-xs text-gray-500 mt-0.5">
                {deliveryNoteLabel ? `${deliveryNoteLabel} · ` : ''}
                {officeStart
                  ? `Start: Office (${officeStart.lat.toFixed(5)}, ${officeStart.lng.toFixed(5)})`
                  : 'Office start not set'}
              </p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-lg hover:bg-white/80 text-gray-500">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-3 border-b border-gray-100 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={handleReroute}
            disabled={loading || saving || !officeStart || rawStops.length === 0}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-teal-700 text-white hover:bg-teal-800 disabled:opacity-50"
            title="Re-run nearest-neighbor from office"
          >
            <Navigation className="w-4 h-4" />
            Reroute
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={loading || saving || !result?.route?.length || (mode === 'view' && !dirty && result?.fromSaved)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-primary text-white hover:bg-green-700 disabled:opacity-50"
          >
            {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {mode === 'optimize' ? 'Confirm Route' : 'Save Route'}
          </button>
          {info ? <span className="text-sm text-gray-600 ml-auto">{info}</span> : null}
        </div>

        {error && (
          <div className="mx-6 mt-3 flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <div className="px-6 py-4 overflow-auto flex-1 space-y-4">
          {loading ? (
            <div className="py-16 text-center text-gray-500">
              <RefreshCw className="w-8 h-8 text-primary animate-spin mx-auto mb-2" />
              Loading route…
            </div>
          ) : result ? (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                  <div className="text-xs text-gray-500">Stops</div>
                  <div className="text-lg font-semibold text-gray-900">{result.route.length}</div>
                </div>
                <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                  <div className="text-xs text-gray-500">Total distance</div>
                  <div className="text-lg font-semibold text-gray-900">{formatDistanceKm(result.totalDistanceKm)}</div>
                </div>
                <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                  <div className="text-xs text-gray-500 flex items-center gap-1">
                    <Clock className="w-3 h-3" /> Est. drive time
                  </div>
                  <div className="text-lg font-semibold text-gray-900">{formatDurationMin(result.totalDurationMin)}</div>
                </div>
                <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                  <div className="text-xs text-gray-500">Missing coords</div>
                  <div className="text-lg font-semibold text-gray-900">{result.withoutCoords.length}</div>
                </div>
              </div>

              <div className="rounded-lg overflow-hidden border border-gray-200" style={{ height: 260 }}>
                <MapContainer
                  center={[result.start.lat, result.start.lng]}
                  zoom={10}
                  style={{ height: '100%', width: '100%' }}
                  scrollWheelZoom
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <FitBounds points={mapPoints} />
                  <Marker position={[result.start.lat, result.start.lng]} icon={createStopIcon('S', '#111827')}>
                    <Popup>
                      <strong>{result.start.label}</strong>
                    </Popup>
                  </Marker>
                  {result.route.map((stop) =>
                    stop.lat != null && stop.lng != null ? (
                      <Marker
                        key={stop.id || stop.stopNo}
                        position={[stop.lat, stop.lng]}
                        icon={createStopIcon(String(stop.stopNo), '#46955A')}
                      >
                        <Popup>
                          <strong>
                            #{stop.stopNo} {stop.voucherNo}
                          </strong>
                          <br />
                          {stop.locationName}
                          <br />
                          From prev: {formatDistanceKm(stop.distanceFromPrevKm)} ·{' '}
                          {formatDurationMin(stop.durationFromPrevMin)}
                        </Popup>
                      </Marker>
                    ) : null
                  )}
                  {mapPoints.length > 1 && (
                    <Polyline positions={mapPoints} pathOptions={{ color: '#46955A', weight: 4, opacity: 0.85 }} />
                  )}
                </MapContainer>
              </div>

              <div className="overflow-x-auto border border-gray-200 rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-2 py-2 w-8" />
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Voucher</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">From prev</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Leg time</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Cumulative</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    <tr className="bg-gray-50">
                      <td className="px-2 py-2" />
                      <td className="px-3 py-2 font-semibold text-gray-800">S</td>
                      <td className="px-3 py-2 text-gray-700" colSpan={2}>
                        {result.start.label}
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-600" colSpan={4}>
                        {result.start.lat.toFixed(6)}, {result.start.lng.toFixed(6)}
                      </td>
                    </tr>
                    {result.route.map((stop, index) => (
                      <tr
                        key={stop.id || stop.stopNo}
                        className="hover:bg-gray-50"
                        draggable
                        onDragStart={() => onDragStart(index)}
                        onDragOver={onDragOver}
                        onDrop={() => onDrop(index)}
                      >
                        <td className="px-2 py-2 text-gray-400 cursor-grab active:cursor-grabbing">
                          <GripVertical className="w-4 h-4" />
                        </td>
                        <td className="px-3 py-2">
                          <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-primary text-white text-xs font-bold">
                            {stop.stopNo}
                          </span>
                        </td>
                        <td className="px-3 py-2 font-medium text-gray-900">{stop.voucherNo || '—'}</td>
                        <td className="px-3 py-2 text-gray-700">{stop.customerName || '—'}</td>
                        <td className="px-3 py-2 text-gray-700">
                          <div className="flex items-start gap-1">
                            <MapPin className="w-3.5 h-3.5 text-primary mt-0.5 flex-shrink-0" />
                            <div>
                              <div>{stop.locationName || '—'}</div>
                              <div className="text-xs text-gray-500">{stop.emirateName || ''}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">{formatDistanceKm(stop.distanceFromPrevKm)}</td>
                        <td className="px-3 py-2 whitespace-nowrap">{formatDurationMin(stop.durationFromPrevMin)}</td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          {formatDistanceKm(stop.cumulativeKm)}
                          <div className="text-xs text-gray-500">{formatDurationMin(stop.cumulativeMin)}</div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {result.withoutCoords.length > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
                  <div className="text-sm font-medium text-amber-900 mb-2">
                    Orders without location coordinates (excluded from map)
                  </div>
                  <ul className="text-sm text-amber-800 space-y-1">
                    {result.withoutCoords.map((o) => (
                      <li key={o.id || o.orderId}>
                        {o.voucherNo || '—'}
                        {o.locationName ? ` · ${o.locationName}` : ''}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <p className="text-xs text-gray-500">
                Drag rows to change delivery order. Distances recalculate automatically. Drive time assumes ~
                {result.speedKmh || AVG_SPEED_KMH} km/h.
              </p>
            </>
          ) : null}
        </div>

        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 rounded-b-lg flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default OptimizedRouteModal;
